import { Launch } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store";
import {
  CompositeFilterDescriptor,
  DataResult,
  process,
} from "@progress/kendo-data-query";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DataGrid } from "../../components/DataTable/DataGrid";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { fetchCropTypes } from "../../slices/productcostapplication.slice";
import { EditContractPriceModal } from "./EditContractPriceModal";
import { getContractPriceData } from "../../slices/contractprice.slice";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

export const ContractPrice = () => {
  const dispatch = useAppDispatch();
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilters);
  const exportRef = useRef<ExcelExport | null>(null);
  const { contractPrices } = useAppSelector(
    (state: RootState) => state.contractPrice
  );
  const [selectedYear, setSelectedYear] = useState<string>("All");
  const [selectedCrop, setSelectedCrop] = useState<string>("All");
  const { cropTypesArray } = useAppSelector(
    (state: RootState) => state.applicationdropdown
  );

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);

  const onEditClick = (dataItem: any) => {
    setEditItem(dataItem);
    setOpen(true);
  };

  // *** HELPER FUNCTION: Format contract details for display ***
  const formatContractDetails = (periods: any[]) => {
    if (!periods || periods.length === 0) {
      return "No contracts";
    }
    
    return periods.map((period: any) => 
      `$${period.price?.toFixed(2) || '0.00'}/bu - ${period.bushels || 0}bu (${period.contractDate || 'N/A'})`
    ).join(', ');
  };

  // *** PREPARE DATA WITH FORMATTED CONTRACT DETAILS ***
  const processedContractPrices = contractPrices.map(item => ({
    ...item,
    contractDetails: formatContractDetails(item.periods)
  }));

  const gridColumns: GridColumnProps[] = [
    {
      field: "cropName",
      title: "Crop Name",
      width: "150px",
    },
    {
      field: "year",
      title: "Year",
      width: "100px",
    },
    {
      field: "weightedAveragePrice",
      title: "Weighted Avg Price",
      format: "{0:c}",
      width: "150px",
    },
    {
      field: "finalPrice",
      title: "Final Price",
      format: "{0:c}",
      width: "120px",
    },
    {
      field: "contractDetails",
      title: "Contract Details",
      width: "400px",
    },
    {
      title: "Actions",
      width: "100px",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={onEditClick} />
        ),
      },
    },
  ];

  // Get unique years from contract prices
  const availableYears = Array.from(
    new Set(contractPrices.map((item: any) => item.year))
  ).sort((a, b) => b - a);

  useEffect(() => {
    dispatch(getContractPriceData());
    dispatch(fetchCropTypes({}));
  }, []);

  useEffect(() => {
    if (selectedCrop || selectedYear) {
      const filterObj: CompositeFilterDescriptor = {
        logic: "and",
        filters: [],
      };
      if (selectedYear !== "All") {
        filterObj.filters.push({
          field: "year",
          operator: "eq",
          value: parseInt(selectedYear),
        });
      }
      if (selectedCrop !== "All") {
        filterObj.filters.push({
          field: "cropName",
          operator: "contains",
          value: selectedCrop,
        });
      }
      setFilter(filterObj);
    } else {
      setFilter(initialFilters);
    }
  }, [selectedCrop, selectedYear]);

  const handleClose = () => {
    dispatch(getContractPriceData());
    setOpen(false);
  };

  // *** SIMPLIFIED EXPORT FUNCTION ***
  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(processedContractPrices, {
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <FormControl>
          <InputLabel>Crop</InputLabel>
          <Select
            label="Crop"
            value={selectedCrop}
            onChange={(e: SelectChangeEvent) => setSelectedCrop(e.target.value)}
            size="small"
            sx={{ minWidth: "200px" }}
          >
            <MenuItem value="All">All</MenuItem>
            {cropTypesArray.map((cropType) => (
              <MenuItem key={cropType.id} value={cropType.cropName}>
                {cropType.cropName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <InputLabel>Year</InputLabel>
          <Select
            label="Year"
            value={selectedYear}
            onChange={(e: SelectChangeEvent) => setSelectedYear(e.target.value)}
            size="small"
            sx={{ minWidth: "150px" }}
          >
            <MenuItem value="All">All</MenuItem>
            {availableYears.map((year) => (
              <MenuItem key={year} value={year.toString()}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
        >
          Export
        </Button>
      </Box>
      <Box>
        <DataGrid
          ref={exportRef}
          data={processedContractPrices}
          dataItemKey="id"
          filter={filter}
          columns={gridColumns}
        />
      </Box>
      {open && (
        <EditContractPriceModal
          open={open}
          handleClose={handleClose}
          editItem={editItem}
        />
      )}
    </Box>
  );
};